import React from 'react';
import { useNavigate } from 'react-router-dom';
import { STATUS_OPTIONS } from '../constants';
import ActionMenu from '../../../shared/ActionMenu/ActionMenu';
import StatusBadge from '../../../shared/StatusBadge/StatusBadge';

/**
 * Table row component for ProjectList, rendering a single project.
 * 
 * @param {Object} props
 * @param {Object} props.project
 * @param {boolean} props.showBulk
 * @param {Object} props.globalPermissions
 * @param {Array} props.selectedProjectIds
 * @param {Function} props.setSelectedProjectIds
 * @param {Function} props.onProjectSelect
 * @param {Function} props.setSelectedProject
 * @param {Function} props.setProjectMode
 * @param {Function} props.setShowProjectModal
 * @param {Function} props.setFocusedProject
 * @param {Function} props.setViewMode
 * @param {Function} props.handleDeleteProject
 * @param {string} props.variant
 */
export default function ProjectTableRow({
    project,
    showBulk,
    globalPermissions,
    selectedProjectIds = [],
    setSelectedProjectIds,
    onProjectSelect,
    setSelectedProject,
    setProjectMode,
    setShowProjectModal,
    setFocusedProject,
    setViewMode,
    handleDeleteProject,
    handleRestoreProject,
    handleHardDelete,
    isTrashView,
    variant
}) {
    const navigate = useNavigate();
    const p = project; // Shortcut

    const actions = isTrashView ? [
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
            ),
            label: 'Öffnen',
            onClick: () => {
                setSelectedProject(p);
                setProjectMode('view');
                setShowProjectModal(true);
            },
            tooltip: 'Ansehen'
        },
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
            ),
            label: 'Wiederherstellen',
            onClick: () => handleRestoreProject(p.id),
            tooltip: 'Wiederherstellen'
        },
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
            ),
            label: 'Dauerhaft löschen',
            variant: 'delete',
            onClick: () => handleHardDelete(p.id),
            tooltip: 'Dauerhaft löschen'
        }
    ] : [
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                    <path d="M9 12H4s.55-3.03 2-4.5" />
                    <path d="M12 15v5s3.03-.55 4.5-2" />
                </svg>
            ),
            label: onProjectSelect ? 'Öffnen' : 'Im Bautagebuch öffnen',
            onClick: () => {
                if (onProjectSelect) {
                    onProjectSelect(p);
                } else {
                    navigate(`/bautagebuch?project=${p.id}`);
                }
            },
            tooltip: onProjectSelect ? 'Bautagebuch öffnen' : 'Im Bautagebuch öffnen'
        },
        { isDivider: true },
        {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
            ),
            label: 'Verwalten',
            onClick: () => {
                setSelectedProject(p);
                setProjectMode(p.can_edit_project ? 'edit' : 'view');
                setShowProjectModal(true);
            },
            tooltip: 'Verwalten'
        },
        globalPermissions.delete_projects && {
            icon: (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
            ),
            label: 'Löschen',
            variant: 'delete',
            onClick: () => handleDeleteProject(p),
            tooltip: 'Löschen'
        }
    ].filter(Boolean);

    return (
        <tr className={`${p.status === 'archived' ? 'row-archived' : ''} ${isTrashView ? 'row-trash' : ''}`}
            onClick={(e) => {
                if (isTrashView) {
                    setSelectedProject(p);
                    setProjectMode('view');
                    setShowProjectModal(true);
                    return;
                }
                if (onProjectSelect) {
                    onProjectSelect(p);
                } else {
                    setSelectedProject(p);
                    setProjectMode((p.can_edit_project || globalPermissions.edit_projects) ? 'edit' : 'view');
                    setShowProjectModal(true);
                }
            }}
            style={{ ...(selectedProjectIds.includes(p.id) ? { background: '#f0f9ff' } : {}), cursor: 'pointer' }}
        >
            {showBulk && globalPermissions.delete_projects && (
                <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <input type="checkbox" checked={selectedProjectIds.includes(p.id)} onChange={() => {
                            setSelectedProjectIds(prev => prev.includes(p.id) ? prev.filter(pid => pid !== p.id) : [...prev, p.id]);
                        }} />
                    </div>
                </td>
            )}
            <td className="align-center"><StatusBadge status={p.status} /></td>
            <td>{p.project_number}</td>
            <td style={{ fontWeight: 'bold', color: variant === 'tool' ? '#4f46e5' : undefined }}>{p.name}</td>
            <td>
                {isTrashView ? (
                    <span style={{ fontSize: '0.85rem', color: '#86868b' }}>{p.address}</span>
                ) : (
                    <span
                        style={{ fontSize: '0.85rem', color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}
                        title="Auf Karte zeigen"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (p.latitude && p.longitude) {
                                setFocusedProject(p);
                                setViewMode('map');
                            } else {
                                alert('Dieses Projekt hat keine Standortdaten.');
                            }
                        }}
                    >
                        {p.address}
                    </span>
                )}
            </td>
            <td className="align-center">
                <ActionMenu actions={actions} />
            </td>
        </tr>
    );
}
